import { Button, Card, Container, Flex, Grid, Table, TextInput, Title } from '@mantine/core';
import { IconBuilding, IconPlus, IconSearch } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import CompanyRows from '../../components/Companies/CompanyRows';
import Metadata from '../../components/Metadata';
import Pager from '../../components/Pager/Pager';
import PageSizeOptions from '../../constants/pageSizeOptions';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const fetchDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, filter, perPage) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/companies?size=${perPage}&${queryString.stringify(filter)}`
    ),
  300
);

const CompaniesPage = () => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();
  const { accountId, domainId } = useParams();

  const [filter, setFilter] = useState({ from: 0, showAnonymous: false });
  const [companies, setCompanies] = useState();
  const [totalCount, setTotalCount] = useState();
  const [pageSize, setPerPage] = useState(PageSizeOptions[0]);
  const [pageCount, setPageCount] = useState();

  useEffect(() => {
    const parsedSearch = queryString.parse(location.search);
    setFilter({
      ...parsedSearch,
      from: parseInt(parsedSearch.from || '0'),
      showAnonymous: parsedSearch.showAnonymous === 'true',
    });
    setPerPage(parsedSearch.pageSize || PageSizeOptions[0]);
    setCompanies(undefined);
    fetchDebounced(fetch, accountId, domainId, queryString.parse(location.search), pageSize).then(
      ({ companies, totalCount }) => {
        setCompanies(companies);
        setTotalCount(totalCount);
        setPageCount(Math.ceil(totalCount / pageSize));
      }
    );
  }, [accountId, domainId, fetch, location, pageSize]);

  const search = (e) => {
    e.preventDefault();
    navigate({ search: queryString.stringify({ ...filter }) });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFilter = { ...filter, [name]: value, from: 0 };

    setFilter(newFilter);
  };

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    const newOffset = (selectedPage - 1) * pageSize;
    if (newOffset !== filter.from) {
      navigate({ search: queryString.stringify({ ...filter, from: (selectedPage - 1) * pageSize }) });
    }
  };

  const setPageSizeClick = (pageSize) => {
    setPerPage(pageSize);
    navigate({ search: queryString.stringify({ ...filter, from: 0, pageSize }) });
  };

  const common = (
    <>
      <Card withBorder mb="md">
        <Card.Section inheritPadding py="sm">
          <Container>
            <form onSubmit={(e) => search(e)}>
              <Grid>
                <Grid.Col span={10}>
                  <TextInput
                    name="query"
                    value={filter.query || ''}
                    type="text"
                    onChange={(e) => handleChange({ target: { name: 'query', value: e.target.value } })}
                    placeholder="Search by Company ID, name, email, or phone..."
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <Button fullWidth style={{ float: 'right' }} type="submit" disabled={!companies || !filter.query}>
                    <IconSearch size={14} /> Search
                  </Button>
                </Grid.Col>
              </Grid>
            </form>
          </Container>
        </Card.Section>
      </Card>
    </>
  );

  return (
    <>
      <Metadata title={`${Page['companies']}`} />
      <Grid>
        <Grid.Col span={3}></Grid.Col>
        <Grid.Col span={6}>{common}</Grid.Col>
      </Grid>
      <Card mb="sm" data-tour={companies ? 'companiesPageTable' : undefined} withBorder>
        <Card.Section inheritPadding py="md">
          <Flex justify="space-between" align="center">
            {totalCount !== undefined ? (
              <Title order={5}>
                <IconBuilding size={16} /> {totalCount.toLocaleString(navigator.language)} Companies
              </Title>
            ) : (
              'Loading...'
            )}
            <Button variant="default" disabled={!companies} onClick={() => navigate(location.pathname + '/new')}>
              <IconPlus size={16} style={{ marginRight: 4 }} /> Add Company
            </Button>
          </Flex>
        </Card.Section>
        {companies && (
          <Table striped withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Phone number</Table.Th>
                <Table.Th>Last Change At</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <CompanyRows companies={companies} />
            </Table.Tbody>
          </Table>
        )}
      </Card>
      <Pager
        pageSize={pageSize}
        pageCount={pageCount}
        onPageChange={handlePageClick.bind(this)}
        initialPage={Math.round(parseInt(filter.from || '0') / pageSize + 1)}
        setPageSize={setPageSizeClick}
      />
    </>
  );
};

export default CompaniesPage;
